import { FieldType } from '@/components/table/_fieldDefinitions/FieldType.ts';
import {
  FieldDefinitionDto,
  TableDefinitionDto,
} from '@/components/table/types/TableDefinitionDto.ts';
import { TablePreferences } from '@/components/table/preferences/tablePreferences.ts';
import {
  isPrimaryField,
  resolveFieldImportance,
} from '@/components/table/preferences/fieldImportance.ts';

/** Id of the (optional) bulk-selection checkbox column. */
export const SELECT_COLUMN_ID = 'select';
/** Id of the (optional) row context-menu column. */
export const OPTIONS_COLUMN_ID = 'options';

/**
 * Whether a backend field is rendered as a real column. ID fields are used
 * for filtering only and never shown.
 */
export function isVisibleColumn(field: FieldDefinitionDto): boolean {
  return field.type !== FieldType.ID;
}

/**
 * Resolves the TanStack column id for a backend field.
 *
 * NOTE: this must stay in sync with the id logic in `useColumns.buildColumns`.
 */
export function getColumnId(field: FieldDefinitionDto): string {
  let id = field.fieldId;
  if (field.customField) {
    id = `customValue.${field.fieldId.split('.')[1]}`;
  }
  if (field.fieldId.includes('optionValue')) {
    id = `optionValue.${field.fieldId.split('.')[1]}`;
  }
  return id;
}

export interface TableSystemColumns {
  /** A bulk-selection column is present (bulk actions enabled). */
  hasSelect: boolean;
  /** A row context-menu column is present. */
  hasOptions: boolean;
}

const dataFieldsOf = (tableDefinition: TableDefinitionDto): FieldDefinitionDto[] =>
  (tableDefinition.fields ?? []).filter(isVisibleColumn);

/**
 * Builds the default column preferences straight from the backend table
 * definition — the state a table shows when the user has never customised it.
 */
export function buildDefaultColumnPreferences(
  tableDefinition: TableDefinitionDto,
  system: TableSystemColumns,
): TablePreferences {
  const dataFields = dataFieldsOf(tableDefinition);
  const importance = resolveFieldImportance(tableDefinition.fields ?? []);

  // Primary columns are kept first so they never end up after a free column.
  const primary = dataFields.filter((f) => isPrimaryField(f, importance));
  const rest = dataFields.filter((f) => !isPrimaryField(f, importance));
  const orderedFields = [...primary, ...rest];

  const columnOrder: string[] = [
    ...(system.hasSelect ? [SELECT_COLUMN_ID] : []),
    ...orderedFields.map(getColumnId),
    ...(system.hasOptions ? [OPTIONS_COLUMN_ID] : []),
  ];

  const columnVisibility: Record<string, boolean> = {};
  const columnSizing: Record<string, number> = {};
  for (const field of dataFields) {
    const id = getColumnId(field);
    columnVisibility[id] = isPrimaryField(field, importance)
      ? true
      : !field.defaultHidden;
    if (field.colSize) {
      columnSizing[id] = field.colSize;
    }
  }

  return { columnOrder, columnVisibility, columnSizing };
}

/**
 * Enforces the invariants that must always hold, regardless of what the user
 * (or a stale persisted entry) asked for:
 *  - primary / non-hideable columns are always visible;
 *  - primary columns keep their position at the front;
 *  - at least one data column stays visible;
 *  - every column has a visibility entry, sizes fall back to backend defaults.
 */
function normalizeColumnPreferences(
  prefs: TablePreferences,
  tableDefinition: TableDefinitionDto,
  defaults: TablePreferences,
): TablePreferences {
  const dataFields = dataFieldsOf(tableDefinition);
  const importance = resolveFieldImportance(tableDefinition.fields ?? []);

  const columnVisibility: Record<string, boolean> = {
    ...prefs.columnVisibility,
  };
  for (const field of dataFields) {
    const id = getColumnId(field);
    const hideable = field.hideable ?? true;
    if (isPrimaryField(field, importance) || !hideable) {
      columnVisibility[id] = true;
    } else if (columnVisibility[id] === undefined) {
      columnVisibility[id] = defaults.columnVisibility[id] ?? true;
    }
  }

  const anyVisible = dataFields.some((f) => columnVisibility[getColumnId(f)]);
  const safeVisibility = anyVisible
    ? columnVisibility
    : { ...defaults.columnVisibility };

  // Re-derive order: select first, primary columns next (backend order), the
  // remaining columns in the user's saved order, options last.
  const primaryIds = dataFields
    .filter((f) => isPrimaryField(f, importance))
    .map(getColumnId);
  const primarySet = new Set(primaryIds);
  const knownDataIds = new Set(dataFields.map(getColumnId));
  const restInSavedOrder = prefs.columnOrder.filter(
    (id) => knownDataIds.has(id) && !primarySet.has(id),
  );
  const columnOrder: string[] = [
    ...(prefs.columnOrder.includes(SELECT_COLUMN_ID) ? [SELECT_COLUMN_ID] : []),
    ...primaryIds,
    ...restInSavedOrder,
    ...(prefs.columnOrder.includes(OPTIONS_COLUMN_ID)
      ? [OPTIONS_COLUMN_ID]
      : []),
  ];

  return {
    columnOrder,
    columnVisibility: safeVisibility,
    columnSizing: { ...defaults.columnSizing, ...prefs.columnSizing },
  };
}

/**
 * Reconciles persisted preferences with the current backend table definition.
 *
 * If the persisted entry describes a different set of columns than the backend
 * now returns (column added / removed / renamed) it is considered stale and
 * the defaults are used instead — `wasReset` signals that to the caller so the
 * stale entry can be cleared.
 */
export function resolveColumnPreferences(
  persisted: TablePreferences | null,
  tableDefinition: TableDefinitionDto,
  system: TableSystemColumns,
): { preferences: TablePreferences; wasReset: boolean } {
  const defaults = buildDefaultColumnPreferences(tableDefinition, system);

  if (!persisted) {
    return { preferences: defaults, wasReset: false };
  }

  const persistedIds = [...persisted.columnOrder].sort();
  const defaultIds = [...defaults.columnOrder].sort();
  const idsMatch =
    persistedIds.length === defaultIds.length &&
    persistedIds.every((id, index) => id === defaultIds[index]);

  if (!idsMatch) {
    return { preferences: defaults, wasReset: true };
  }

  return {
    preferences: normalizeColumnPreferences(persisted, tableDefinition, defaults),
    wasReset: false,
  };
}
